import { Component, computed, DestroyRef, inject, signal, ViewChild } from '@angular/core';

import { RouterOutlet } from '@angular/router';
import { type MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppHeader } from '../components/app-header/app-header';
import { AppSidebar } from '../components/app-sidebar/app-sidebar';
import { type NavItem } from '../layouts.interface';
import { TranslationService } from '../../core/services/translation-service';
import { TranslateService } from '@ngx-translate/core';
import { langToStandardMap } from '../../core/helpers/i18n/lang-mappers';
import { AuthService } from '../../core/auth/auth-service';
import { RoleId } from '../../features/users/enums/role.enum';
import { SessionService } from '../../core/auth/session-service';
import { ROLE_TRANSLATION_KEY } from '../../features/users/utils/role.utils';
import { DirectionService } from '../../core/services/direction-service';

@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, MatSidenavModule, AppHeader, AppSidebar],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.css',
})
export class MainLayout {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private translationService = inject(TranslationService);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private readonly directionService = inject(DirectionService);

  readonly direction = this.directionService.direction;

  readonly currentLang = signal(this.translationService.currentLang(this.translateService));

  @ViewChild('sidenav') sidenav?: MatSidenav;

  readonly isMobile = signal(false);

  readonly appNameKey = 'shell.appName';
  readonly logoUrl = '/assets/img/icons/logo_instructo.svg';

  readonly userLogged = this.sessionService.getUser()!;

  readonly roleName = ROLE_TRANSLATION_KEY[this.userLogged.fkIdRol];

  readonly baseMenuItems: NavItem[] = [
    {
      id: 'home',
      label: 'shell.navigation.home',
      icon: 'home',
      route: 'home',
      allowedRoles: [
        RoleId.Admin,
        RoleId.NationalOffice,
        RoleId.DevelopmentChief,
        RoleId.Instructor,
      ],
    },
    {
      id: 'course-planning',
      label: 'shell.navigation.coursePlanning',
      icon: 'edit_document',
      route: 'course-planning',
      allowedRoles: [
        RoleId.Admin,
        RoleId.NationalOffice,
        RoleId.DevelopmentChief,
        RoleId.Instructor,
      ],
    },
    {
      id: 'calendar',
      label: 'shell.navigation.calendar',
      icon: 'event',
      route: 'calendar',
      dividerBefore: true,
      allowedRoles: [
        RoleId.Admin,
        RoleId.NationalOffice,
        RoleId.DevelopmentChief,
        RoleId.Instructor,
      ],
    },
    {
      id: 'qrs',
      label: 'shell.navigation.qrs',
      icon: 'qr_code_scanner',
      route: 'qrs',
      allowedRoles: [
        RoleId.Admin,
        RoleId.NationalOffice,
        RoleId.DevelopmentChief,
        RoleId.Instructor,
      ],
    },
    {
      id: 'attendance',
      label: 'shell.navigation.attendance',
      icon: 'checklist',
      route: 'attendance',
      allowedRoles: [
        RoleId.Admin,
        RoleId.NationalOffice,
        RoleId.DevelopmentChief,
        RoleId.Instructor,
      ],
    },
    {
      id: 'users',
      label: 'shell.navigation.users',
      icon: 'supervisor_account',
      route: 'users',
      dividerBefore: true,
      allowedRoles: [RoleId.Admin, RoleId.NationalOffice],
    },
    {
      id: 'events',
      label: 'shell.navigation.events',
      icon: 'event_note',
      route: 'events',
      dividerBefore: true,
      allowedRoles: [RoleId.Admin],
    },
    {
      id: 'catalogs',
      label: 'shell.navigation.catalogs',
      icon: 'library_books',
      route: 'catalogs',
      allowedRoles: [RoleId.Admin, RoleId.NationalOffice],
    },
  ];

  readonly menuItems = computed<NavItem[]>(() => {
    const lang = langToStandardMap[this.currentLang()];

    return this.buildMenu(this.baseMenuItems, [], lang);
  });

  constructor() {
    this.breakpointObserver
      .observe('(max-width: 1023px)')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.isMobile.set(state.matches);

        if (state.matches) {
          this.sidenav?.close();
        } else {
          this.sidenav?.open();
        }
      });

    this.translateService.onLangChange.subscribe((event) => {
      this.currentLang.set(event.lang);
    });
  }

  private buildMenu(items: NavItem[], parentPath: string[] = [], lang: string): NavItem[] {
    return items.map((item) => {
      const currentPath = [...parentPath, item.route];

      const newItem: NavItem = {
        ...item,
        fullRoute: ['/', lang, ...currentPath],
      };

      if (item.children?.length) {
        newItem.children = this.buildMenu(item.children, currentPath, lang);
      }

      return newItem;
    });
  }

  toggleSidebar(): void {
    this.sidenav?.toggle();
  }

  closeSidebarOnMobile(): void {
    if (this.isMobile()) {
      this.sidenav?.close();
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
